import { motion } from "motion/react"
import { OrderHistoryItem } from "../../types/entities/orderHistory"

import { formatOrderNumber } from "../../features/tickets/ticketUtils"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

function formatDate(raw: string | number): string {
    try {
        const d = typeof raw === "number" ? new Date(raw * 1000) : new Date(raw)
        if (isNaN(d.getTime())) return String(raw)
        return d.toLocaleString("ru-RU", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
    } catch {
        return String(raw)
    }
}

function getStatusColor(name: string): string {
    const n = name.toLowerCase()
    if (n.includes("оплач") || n.includes("успеш")) return "#29cc6a"
    if (n.includes("отмен") || n.includes("возврат") || n.includes("верн")) return "#e4206b"
    if (n.includes("ожид") || n.includes("обраб")) return "#f5a623"
    return "#349be6"
}

function fmt(n: number) {
    return n.toLocaleString("ru-RU", { maximumFractionDigits: 0 })
}

type RowProps = {
    label: string
    value: string
    accent?: string
    muted?: boolean
    big?: boolean
}

function Row({ label, value, accent, muted, big }: RowProps) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span
                className="text-[13px]"
                style={{ fontFamily: BODY, color: "rgba(255,255,255,0.5)" }}>
                {label}
            </span>
            <span
                className="shrink-0"
                style={{
                    fontFamily: MONO,
                    fontWeight: big ? 800 : 700,
                    fontSize: big ? 16 : 13,
                    color: accent ?? (muted ? "rgba(255,255,255,0.3)" : "#fff"),
                }}>
                {value}
            </span>
        </div>
    )
}

export default function HistoryItem({ item, index = 0 }: { item: OrderHistoryItem; index?: number }) {
    const statusColor = getStatusColor(item.state_name)
    const hasBonuses = item.amount.bonus_pay > 0 || item.amount.bonus_add > 0

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            viewport={{ once: true }}
            className="rounded-[20px] border border-white/10 bg-white/[0.05] overflow-hidden">

            {/* ── Header: номер, дата, статус ── */}
            <div className="px-5 py-4 border-b border-white/[0.08] flex items-start justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                    <span
                        className="text-white text-[14px]"
                        style={{ fontFamily: MONO, fontWeight: 700, letterSpacing: "0.04em" }}>
                        #{formatOrderNumber(item.number)}
                    </span>
                    <span
                        className="text-white/35 text-[11px]"
                        style={{ fontFamily: MONO }}>
                        {formatDate(item.created ?? item.created_at)}
                    </span>
                </div>
                <span
                    className="shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold"
                    style={{
                        fontFamily: MONO,
                        fontWeight: 700,
                        color: statusColor,
                        background: `${statusColor}18`,
                        border: `1px solid ${statusColor}40`,
                    }}>
                    {item.state_name}
                </span>
            </div>

            {/* ── Body: фильм + описание ── */}
            <div className="px-5 py-4 border-b border-white/[0.08]">
                <p
                    className="text-white leading-snug mb-1"
                    style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 15, letterSpacing: "-0.01em" }}>
                    {item.film_name}
                </p>
                {item.session_datetime > 0 && (
                    <p
                        className="text-white/70 text-[12px] mb-1"
                        style={{ fontFamily: MONO, fontWeight: 600 }}>
                        {formatDate(item.session_datetime)}
                    </p>
                )}
                <p
                    className="text-white/45 text-[12px] leading-relaxed"
                    style={{ fontFamily: BODY }}>
                    {item.description}
                </p>
            </div>

            {/* ── Financials ── */}
            <div className="px-5 py-4 flex flex-col gap-2.5">
                {/* Цена без скидок */}
                <Row label="Цена билетов" value={`${fmt(item.amount.all)} ₽`} />

                {/* Бонусы — всегда показываем блок если есть хоть что-то */}
                {hasBonuses && (
                    <div
                        className="rounded-[12px] px-4 py-3 flex flex-col gap-2 mt-0.5"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                        {item.amount.bonus_pay > 0 && (
                            <Row
                                label="Списано бонусов"
                                value={`−${fmt(item.amount.bonus_pay)}`}
                                accent="#f5a623"
                            />
                        )}
                        {item.amount.bonus_add > 0 && (
                            <Row
                                label="Начислено бонусов"
                                value={`+${fmt(item.amount.bonus_add)}`}
                                accent="#29cc6a"
                            />
                        )}
                    </div>
                )}

                {/* Divider */}
                <div className="h-px bg-white/[0.08] my-0.5" />

                {/* Итого */}
                <Row
                    label="Итого оплачено"
                    value={`${fmt(item.amount.pay)} ₽`}
                    big
                />
            </div>
        </motion.div>
    )
}
